import React, { useState, useEffect, useRef } from 'react';
import './EmailModal.css';
import Select2Dropdown from './Select2Dropdown';
import { sendAndSaveEmail, saveEmailOnly } from '../../services/emailService';
import { getAuthHeaders } from '../../auth';
import { toast } from 'react-toastify';
import { openFilePreview } from './FilePreviewModal';
import ConfirmDialog from './ConfirmDialog';

const getUserFullName = () => {
    try {
        const userDataString = localStorage.getItem('kk_user');
        if (userDataString) {
            const userData = JSON.parse(userDataString);
            return userData.name || userData.username || 'K&K User';
        }
        return 'K&K User';
    } catch {
        return 'K&K User';
    }
};

const getAttachmentDisplayName = (attachment) => {
    let fileName = '';

    if (attachment.document_file) {
        if (typeof attachment.document_file === 'string') {
            const urlParts = attachment.document_file.split('/');
            fileName = urlParts[urlParts.length - 1];
        } else {
            fileName = attachment.document_file.name || attachment.document_file;
        }
    } else {
        fileName = attachment.file_name || attachment.name || 'Unknown File';
    }

    if (fileName.includes('.')) {
        const parts = fileName.split('.');
        const extension = parts.pop();
        const nameWithoutExt = parts.join('.');
        const cleanName = nameWithoutExt.replace(/_[a-zA-Z0-9]+$/, '');
        return `${cleanName}.${extension}`;
    }

    return fileName;
};

const FONT_OPTIONS = ['Segoe UI', 'Arial', 'Aptos', 'Times New Roman', 'Calibri', 'Verdana'];
const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];

// Extract the font a template body uses so the ribbon can reflect it
const detectFontFromHtml = (html) => {
    if (!html) return null;
    try {
        const container = document.createElement('div');
        container.innerHTML = html;
        let font = '';
        const styledEl = [...container.querySelectorAll('[style]')].find((el) => el.style.fontFamily);
        if (styledEl) font = styledEl.style.fontFamily;
        if (!font) {
            const fontEl = container.querySelector('font[face]');
            if (fontEl) font = fontEl.getAttribute('face') || '';
        }
        font = font.split(',')[0].trim().replace(/^["']+|["']+$/g, '');
        return font || null;
    } catch {
        return null;
    }
};

const EmailModal = ({ 
    isOpen, 
    onClose, 
    onSave, 
    editEmail = null,
    readOnly = false,
    defaultFrom = 'software@kkcompany.co.tz',
    defaultSubjectPrefix = '',
    showAttachments = true,
    customerEmailGroups = [],
    jobAttachments = [],
    jobData = null,
    defaultTemplateName = null,
    defaultTo = '',
    subjectBuilder = null,
    fixedAttachment = null,
    restrictToTemplateNames = null,
    sendEmail = null,
    previewAttachments = false,
    forceFrom = null,
    bodyAppendHtml = null,
    bankOptions = null
}) => {
    const bodyRef = useRef(null);
    const modalRef = useRef(null);
    const defaultTemplateApplied = useRef(false); // Ref to prevent re-applying
    const savedRange = useRef(null);
    const [fontFamily, setFontFamily] = useState('Segoe UI');
    const [fontSize, setFontSize] = useState('');
    
    const [emailData, setEmailData] = useState({
        from: forceFrom || localStorage.getItem('kk_email') || defaultFrom ,
        to: '',
        cc: '',
        subject: '',
        body: '',
        date: new Date().toISOString().split('T')[0],
        attachments: [],
        selectedJobAttachments: [],
        selectedTemplateId: null
    });

    // Email Templates State
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [selectedBank, setSelectedBank] = useState(null);

    const handleBankChange = (val) => {
        const valId = typeof val === 'object' && val !== null ? (val.id ?? val.value) : val;
        const bank = (bankOptions || []).find(o => String(o.id) === String(valId)) || null;
        setSelectedBank(bank);
        const append = typeof bodyAppendHtml === 'function' ? bodyAppendHtml(bank) : (bodyAppendHtml || '');
        setEmailData(prev => {
            const base = prev.baseBody !== undefined ? prev.baseBody : prev.body;
            const nextBody = (base || '') + append;
            // The body editor is an uncontrolled contentEditable — write the
            // DOM directly as handleTemplateSelect does, or the change is invisible.
            if (bodyRef.current) bodyRef.current.innerHTML = nextBody;
            return { ...prev, baseBody: base, body: nextBody };
        });
    };
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    // Track which email groups are selected and for which field (To/CC)
    const [selectedGroups, setSelectedGroups] = useState({});
    
    // Track selected job attachments
    const [selectedJobAttachmentIds, setSelectedJobAttachmentIds] = useState(new Set());

    // Document type filter for job attachments
    const [docTypeFilter, setDocTypeFilter] = useState('');

    // Selection order for job attachments (most recently selected first)
    const [selectedAttachmentOrder, setSelectedAttachmentOrder] = useState([]);

    // Confirm send when no attachments are selected
    const [confirmNoAttachments, setConfirmNoAttachments] = useState(false);
    const pendingSubmitRef = useRef(null);

    // Resizable modal state
    const [isResizing, setIsResizing] = useState(false);
    const [modalSize, setModalSize] = useState({ width: 1400, height: 900 });

    // 2. Add a useEffect to automatically select the default template
    useEffect(() => {
        if (isOpen && emailTemplates.length > 0 && !defaultTemplateApplied.current) {
            let templateToApply = null;
            if (defaultTemplateName) {
                templateToApply = emailTemplates.find(t => (t.template_name || '').toLowerCase() === (defaultTemplateName || '').toLowerCase());
            }
            if (!templateToApply && restrictToTemplateNames && restrictToTemplateNames.length > 0 && emailTemplates.length === 1) {
                templateToApply = emailTemplates[0];
            }
            if (templateToApply) {
                console.log(`Applying default template: ${templateToApply.template_name}`);
                handleTemplateSelect(templateToApply.id);
                defaultTemplateApplied.current = true; // Mark as applied
            }
        }
    }, [isOpen, defaultTemplateName, emailTemplates, restrictToTemplateNames]);


    // Load email templates on component mount
    useEffect(() => {
        if (isOpen) {
            loadEmailTemplates();
        }
    }, [isOpen]);

    // Load email templates from API
    const loadEmailTemplates = async () => {
        setLoadingTemplates(true);
        try {
            const response = await fetch('/api/admin/email-templates/', {
                headers: getAuthHeaders()
            });
            
            if (response.ok) {
                const templates = await response.json();
                console.log('Loaded templates:', templates);
                const filteredTemplates = (restrictToTemplateNames && restrictToTemplateNames.length > 0)
                    ? templates.filter(t => restrictToTemplateNames.some(name => (t.template_name || '').toLowerCase() === name.toLowerCase()))
                    : templates;
                setEmailTemplates(filteredTemplates);
            } else {
                const errorText = await response.text();
                console.error('Failed to load email templates:', response.status, errorText);
                // For now, let's not show templates if there's an auth error
                setEmailTemplates([]);
            }
        } catch (error) {
            console.error('Error loading email templates:', error);
            setEmailTemplates([]);
        } finally {
            setLoadingTemplates(false);
        }
    };

    // Handle email template selection
    /*const handleTemplateSelect = (templateId) => {
        console.log('Template selected:', templateId);
        
        if (!templateId || templateId === '') {
            setEmailData(prev => ({
                ...prev,
                selectedTemplateId: null
            }));
            return;
        }

        const selectedTemplate = emailTemplates.find(template => template.id === parseInt(templateId));
        console.log('Found template:', selectedTemplate);
        
        if (selectedTemplate) {
            const clientName = jobData?.customer_name || jobData?.customer?.name || 'Unknown Client';
            const idfNumber = jobData?.idf_no || 'N/A';
            const templateSubject = selectedTemplate.subject || '';
            const generatedSubject = `${clientName} - IDF: ${idfNumber} - ${templateSubject}`;

            setEmailData(prev => ({
                ...prev,
                subject: generatedSubject,
                body: selectedTemplate.body || '',
                selectedTemplateId: templateId
            }));

            if (bodyRef.current) {
                bodyRef.current.innerHTML = selectedTemplate.body || '';
            }
        }
    };*/

    const handleTemplateSelect = (templateId) => {
        if (!templateId) {
            setEmailData(prev => ({ ...prev, selectedTemplateId: null, cc:'', subject: '', body: '' }));
            if (bodyRef.current) bodyRef.current.innerHTML = '';
            setFontFamily('Segoe UI');
            return;
        }

        const selectedTemplate = emailTemplates.find(template => template.id === parseInt(templateId));
        
        if (selectedTemplate) {
            const clientName = jobData?.customer_name || 'Unknown Client';
            const idfNumber = jobData?.idf_no || 'N/A';
            const templateSubject = selectedTemplate.subject || '';
            const templateCC = selectedTemplate.cc_emails || '';
            const customerRef = jobData?.customer_ref;
            const refPart = customerRef ? `REF. NO: ${customerRef} ` : '';
            const generatedSubject = subjectBuilder
                ? subjectBuilder(selectedTemplate)
                : `${clientName} - ${refPart}IDF: ${idfNumber} - ${templateSubject}`;

            let body = selectedTemplate.body || '';
            const templateName = (selectedTemplate.template_name || '').toLowerCase();

            // Append details for specific templates
            if ((templateName.includes('assessment notice') || templateName.includes('wharfage invoice')) && jobData) {
                const detailsToAppend = [
                    { title: 'BL Number', value: jobData.bl_number },
                    { title: 'Invoice Number', value: jobData.invoice_number },
                    { title: 'Vessel', value: jobData.vessel },
                    { title: 'Supplier', value: jobData.supplier },
                    { title: 'ETA', value: jobData.eta ? new Date(jobData.eta).toLocaleDateString() : 'N/A' }
                ].filter(detail => detail.value); // Filter out details with no value

                if (detailsToAppend.length > 0) {
                    const detailsHtml = detailsToAppend.map(detail => 
                        `<p><strong>${detail.title}:</strong> ${detail.value}</p>`
                    ).join('');
                    
                    body += `<br><br>${detailsHtml}`;
                }
            }

            const baseBody = body;
            if (bodyAppendHtml) {
                body += typeof bodyAppendHtml === 'function' ? bodyAppendHtml(selectedBank) : bodyAppendHtml;
            }

            setEmailData(prev => ({
                ...prev,
                cc: templateCC || '',
                subject: generatedSubject,
                body: body,
                baseBody: baseBody,
                selectedTemplateId: templateId
            }));

            if (bodyRef.current) {
                bodyRef.current.innerHTML = body;
            }

            // Ribbon font should reflect the template's own font
            setFontFamily(detectFontFromHtml(body) || 'Segoe UI');
        }
    };

    // Prepare email template options for Select2Dropdown
    const getTemplateOptions = () => {
        if (loadingTemplates) {
            return [{ value: '', label: 'Loading templates...' }];
        }
        
        const options = [{ value: '', label: 'Select a template (optional)' }];
        
        emailTemplates.forEach(template => {
            options.push({
                value: template.id ? template.id.toString() : '',
                label: template.template_name || `Template ${template.id}` || 'Unnamed Template'
            });
        });
        
        console.log('Template options:', options);
        return options;
    };

    useEffect(() => {
        if (isOpen) {
            // Check for any previous debug logs
            try {
                const debugLog = sessionStorage.getItem('email_debug_log');
                if (debugLog) {
                    console.log('Previous email debug log found:', JSON.parse(debugLog));
                    // Don't clear it immediately, let's see what happened
                }
            } catch (err) {
                console.warn('Could not read debug log:', err);
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (editEmail) {
            const newData = {
                ...editEmail,
                date: editEmail.email_date || editEmail.date || new Date().toISOString().split('T')[0],
                from: editEmail.from_email || editEmail.from || defaultFrom,
                to: editEmail.to_email || editEmail.to || '',
                cc: editEmail.cc_email || editEmail.cc || '',
                attachments: editEmail.attachments || [],
                selectedJobAttachments: editEmail.selectedJobAttachments || [],
                selectedTemplateId: null
            };
            setEmailData(newData);
            
            if (bodyRef.current && newData.body) {
                bodyRef.current.innerHTML = newData.body;
            }
        } else {
            // Fix: Ensure the from field gets populated with defaultFrom
            const newData = {
                from: forceFrom || localStorage.getItem('kk_email') || defaultFrom,
                to: defaultTo,
                cc: '',
                subject: '',
                body: '',
                date: new Date().toISOString().split('T')[0],
                attachments: [],
                selectedJobAttachments: [],
                selectedTemplateId: null
            };
            setEmailData(newData);
            
            if (bodyRef.current) {
                bodyRef.current.innerHTML = '';
            }
        }
        
        defaultTemplateApplied.current = false; // Reset the flag when modal opens
        setSelectedGroups({});
        setSelectedJobAttachmentIds(new Set());
        setSelectedAttachmentOrder([]);
        setDocTypeFilter('');
    }, [editEmail, isOpen, defaultFrom, defaultTo, forceFrom]);

    // Resizable functionality
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing || !modalRef.current) return;
            
            const newWidth = e.clientX - modalRef.current.offsetLeft + 10;
            const newHeight = e.clientY - modalRef.current.offsetTop + 10;
            
            setModalSize({
                width: Math.max(1000, Math.min(newWidth, window.innerWidth - 50)),
                height: Math.max(700, Math.min(newHeight, window.innerHeight - 50))
            });
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    // Helper function to append emails without duplicates
    const appendEmails = (existingEmails, newEmails) => {
        if (!existingEmails.trim()) return newEmails;
        
        const existing = existingEmails.split(';').map(email => email.trim()).filter(email => email);
        const toAdd = newEmails.split(';').map(email => email.trim()).filter(email => email);
        
        const combined = [...existing];
        toAdd.forEach(email => {
            if (!combined.includes(email)) {
                combined.push(email);
            }
        });
        
        return combined.join('; ');
    };

    // Helper function to remove specific emails
    const removeEmails = (existingEmails, emailsToRemove) => {
        if (!existingEmails.trim()) return '';
        
        const existing = existingEmails.split(';').map(email => email.trim()).filter(email => email);
        const toRemove = emailsToRemove.split(';').map(email => email.trim()).filter(email => email);
        
        const remaining = existing.filter(email => !toRemove.includes(email));
        return remaining.join('; ');
    };

    // Function to handle email group selection with checkboxes
    const handleEmailGroupSelection = (emails, type, groupIndex, isChecked) => {
        const groupKey = `${groupIndex}_${type}`;
        const otherType = type === 'to' ? 'cc' : 'to';
        const otherKey = `${groupIndex}_${otherType}`;

        if (isChecked) {
            if (selectedGroups[otherKey]) {
                if (otherType === 'to') {
                    setEmailData(prev => ({ 
                        ...prev, 
                        to: removeEmails(prev.to, emails)
                    }));
                } else {
                    setEmailData(prev => ({ 
                        ...prev, 
                        cc: removeEmails(prev.cc, emails)
                    }));
                }
            }

            if (type === 'to') {
                setEmailData(prev => ({ 
                    ...prev, 
                    to: appendEmails(prev.to, emails)
                }));
            } else if (type === 'cc') {
                setEmailData(prev => ({ 
                    ...prev, 
                    cc: appendEmails(prev.cc, emails)
                }));
            }
            
            setSelectedGroups(prev => {
                const newSelected = { ...prev };
                delete newSelected[otherKey];
                newSelected[groupKey] = true;
                return newSelected;
            });
        } else {
            if (type === 'to') {
                setEmailData(prev => ({ 
                    ...prev, 
                    to: removeEmails(prev.to, emails)
                }));
            } else if (type === 'cc') {
                setEmailData(prev => ({ 
                    ...prev, 
                    cc: removeEmails(prev.cc, emails)
                }));
            }
            
            setSelectedGroups(prev => {
                const newSelected = { ...prev };
                delete newSelected[groupKey];
                return newSelected;
            });
        }
    };

    // Handle job attachment selection
    const handleJobAttachmentSelection = (attachment, isChecked) => {
        const newSelectedIds = new Set(selectedJobAttachmentIds);
        
        if (isChecked) {
            newSelectedIds.add(attachment.id);
        } else {
            newSelectedIds.delete(attachment.id);
        }
        
        setSelectedJobAttachmentIds(newSelectedIds);
        setSelectedAttachmentOrder(prev => isChecked
            ? [attachment.id, ...prev.filter(id => id !== attachment.id)]
            : prev.filter(id => id !== attachment.id));
        
        const selectedAttachments = jobAttachments.filter(att => 
            newSelectedIds.has(att.id)
        );
        
        setEmailData(prev => ({
            ...prev,
            selectedJobAttachments: selectedAttachments
        }));
    };

    const openBlobPreview = (blobUrl, name) => {
        const previewWindow = openFilePreview(blobUrl, name);
        if (previewWindow && previewWindow.addEventListener) {
            previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(blobUrl), { once: true });
        } else {
            setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
        }
    };

    const previewSelectedAttachment = async (e, attachment) => {
        e.preventDefault();
        try {
            if (attachment instanceof File) {
                openBlobPreview(URL.createObjectURL(attachment), attachment.name);
                return;
            }
            const url = attachment.document_file_url
                || (typeof attachment.document_file === 'string' ? attachment.document_file : null)
                || attachment.url
                || attachment.attach_url
                || null;
            if (!url) return;
            const res = await fetch(url, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to load document.');
            const blob = await res.blob();
            openBlobPreview(URL.createObjectURL(blob), getAttachmentDisplayName(attachment));
        } catch (err) {
            toast.error(err.message || 'Could not open document.');
        }
    };

    const handleBodyKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (e.shiftKey) {
                // Shift+Enter creates a line break
                e.preventDefault();
                document.execCommand('insertHTML', false, '<br>');
            } else {
                // Enter creates a paragraph break
                e.preventDefault();
                document.execCommand('insertHTML', false, '<br><br>');
            }
            handleBodyInput(e);
        }
    };

    // Strip Word/Outlook clipboard artifacts (conditional comments, <o:p>,
    // MsoNormal classes, meta/style blocks) and source-formatting whitespace
    // so pasted content keeps its formatting without phantom line breaks.
    const sanitizePastedHtml = (html) => {
        const container = document.createElement('div');
        container.innerHTML = html;
        const commentWalker = document.createTreeWalker(container, NodeFilter.SHOW_COMMENT);
        const comments = [];
        while (commentWalker.nextNode()) comments.push(commentWalker.currentNode);
        comments.forEach((n) => n.remove());
        container.querySelectorAll('style, script, meta, link, title, xml, o\\:p').forEach((n) => n.remove());
        container.querySelectorAll('[class]').forEach((el) => {
            if ((el.getAttribute('class') || '').includes('Mso')) el.removeAttribute('class');
        });
        // Remove whitespace-only text nodes that contain a newline — these come
        // from pretty-printed clipboard HTML, not from the source document.
        const textWalker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
        const ws = [];
        while (textWalker.nextNode()) {
            const t = textWalker.currentNode;
            if (!t.nodeValue.trim() && t.nodeValue.includes('\n')) ws.push(t);
        }
        ws.forEach((n) => n.remove());
        return container.innerHTML;
    };

    const handleBodyPaste = (e) => {
        if (readOnly) return;
        e.preventDefault();
        const clipboard = e.clipboardData || window.clipboardData;
        const html = clipboard && clipboard.getData('text/html');
        if (html) {
            document.execCommand('insertHTML', false, sanitizePastedHtml(html));
        } else {
            const text = clipboard ? clipboard.getData('text/plain') : '';
            const escaped = String(text || '')
                .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                .replace(/\r\n|\r|\n/g, '<br>');
            if (escaped) document.execCommand('insertHTML', false, escaped);
        }
        syncBodyToState();
    };

    // Enhanced submit handler to send email and save to database
    /*const handleSubmit = async (e) => {
        // Safer event prevention
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }
        if (e && typeof e.stopPropagation === 'function') {
            e.stopPropagation();
        }
        
        console.log('=== EMAIL SUBMISSION STARTED ===');
        console.log('JobData:', jobData);
        console.log('EmailData:', emailData);
        console.log('File attachments:', emailData.attachments);
        
        try {
            // Validate required fields before attempting to send
            if (!emailData.from || !emailData.to || !emailData.subject) {
                console.log('Validation failed - missing required fields');
                alert('Please fill in all required fields (From, To, Subject)');
                return false;
            }

            console.log('Validation passed');

            const emailPayload = {
                ...emailData,
                email_date: emailData.date,
                from_email: emailData.from,
                to_email: emailData.to,
                cc_email: emailData.cc,
                job_id: jobData?.id
            };

            console.log('Email payload prepared:', emailPayload);

            if (jobData?.id) {                
                
                const result = await sendAndSaveEmail(emailPayload, jobData.id);
                console.log('Send and save result:', result);
                
               
                
                // Handle both success and error responses from the service
                if (result && result.success) {
                    console.log('Email operation successful');
                    alert(result.message || 'Email saved successfully!');
                    onClose();
                } else {
                    const errorMessage = result?.message || 'Unknown error occurred';
                    console.error('Email operation failed:', errorMessage);
                    alert(`Email failed: ${errorMessage}`);
                    // Don't close the modal on error
                    return false;
                }
            } else {
                console.log('Path: Using onSave callback (no job ID)');
                
                if (typeof onSave !== 'function') {
                    console.error('onSave callback is not a function');
                    alert('Save function is not available.');
                    return false;
                }

                console.log('Calling onSave...');
                await onSave(emailPayload);
                console.log('Email saved via onSave callback');
                                               
                alert('Email saved successfully!');
                onClose();
            }
            
        } catch (error) {
            console.error('=== ERROR IN HANDLESUBMIT ===');
            console.error('Error:', error);
            console.error('Error message:', error.message);
            console.error('Error stack:', error.stack);
            
            alert(`Error processing email: ${error.message}`);
            return false;
        }
        
        // Always return false to prevent any form submission
        return false;
    };*/

    const handleSubmit = async (e, confirmedNoAttachments = false) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        if (e && typeof e.stopPropagation === 'function') e.stopPropagation();

        if (!emailData.from || !emailData.to || !emailData.subject) {
            toast.error('Please fill in all required fields (From, To, Subject)');
            return false;
        }

        // When an attachments panel is offered but nothing was selected,
        // confirm the user intends to send the email without attachments.
        const attachmentsPanelShown = showAttachments || (jobAttachments && jobAttachments.length > 0);
        const hasAttachments =
            (emailData.attachments && emailData.attachments.length > 0) ||
            selectedJobAttachmentIds.size > 0 ||
            !!fixedAttachment;
        if (!confirmedNoAttachments && attachmentsPanelShown && !hasAttachments) {
            pendingSubmitRef.current = e;
            setConfirmNoAttachments(true);
            return false;
        }

        const bodyContent = bodyRef.current ? bodyRef.current.innerHTML : emailData.body;
        const userFullName = getUserFullName();
       
        const finalBody = bodyContent;

        try {
            const emailPayload = {
                ...emailData,
                body: finalBody, // Use the final body with signature
                email_date: emailData.date,
                from_email: emailData.from,
                to_email: emailData.to,
                cc_email: emailData.cc,
                job_id: jobData?.id
            };

            if (jobData?.id) {                
                const result = await (sendEmail || sendAndSaveEmail)(emailPayload, jobData.id);
                if (result && result.success) {
                    toast.success(result.message || 'Email sent and saved successfully!');
                    onSave(emailPayload); // Call onSave to trigger parent actions
                    onClose();
                } else {
                    const errorMessage = result?.message || 'Unknown error occurred';
                    toast.error(`Email failed: ${errorMessage}`);
                    return false;
                }
            } else {
                if (typeof onSave !== 'function') {
                    toast.error('Save function is not available.');
                    return false;
                }
                await onSave(emailPayload);
                toast.success('Email data prepared successfully!');
                onClose();
            }
        } catch (error) {
            toast.error(`Error processing email: ${error.message}`);
            return false;
        }
        return false;
    };

    const handleAttachmentChange = (e) => {
        const files = Array.from(e.target.files);
        setEmailData(prev => ({
            ...prev,
            attachments: files
        }));
    };

    const handleBodyInput = (e) => {
        let content = e.target.innerHTML;

        // Convert div tags to paragraphs (some browsers use divs for line breaks)
        content = content.replace(/<div>/g, '<p>').replace(/<\/div>/g, '</p>');

        // Clean up empty paragraphs but preserve intentional breaks.
        // Source newlines inside the markup are NOT converted — they are
        // formatting artifacts (e.g. from pasted Word/Outlook HTML), not
        // user-intended line breaks.
        content = content.replace(/<p><\/p>/g, '<br>');

        setEmailData(prev => ({ ...prev, body: content }));
    };

    const syncBodyToState = () => {
        if (!bodyRef.current) return;
        let content = bodyRef.current.innerHTML;
        content = content.replace(/<div>/g, '<p>').replace(/<\/div>/g, '</p>');
        content = content.replace(/<p><\/p>/g, '<br>');
        setEmailData(prev => ({ ...prev, body: content }));
    };

    const formatEmailBody = (command, value = null) => {
        document.execCommand(command, false, value);

        // Special handling for Enter key to create proper line breaks
        if (command === 'insertLineBreak') {
            document.execCommand('insertHTML', false, '<br><br>');
        }

        syncBodyToState();
    };

    // Selecting a toolbar dropdown/color picker moves focus out of the
    // editor and collapses the selection — capture it first, then restore
    // before running execCommand.
    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel.rangeCount > 0 && bodyRef.current && bodyRef.current.contains(sel.anchorNode)) {
            savedRange.current = sel.getRangeAt(0).cloneRange();
        }
    };

    const restoreSelection = () => {
        if (!savedRange.current) return;
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedRange.current);
    };

    const applyFontName = (font) => {
        setFontFamily(font);
        bodyRef.current.focus();
        restoreSelection();
        formatEmailBody('fontName', font);
    };

    const applyFontSize = (px) => {
        if (!bodyRef.current || !px) return;
        setFontSize(px);
        bodyRef.current.focus();
        restoreSelection();
        // execCommand('fontSize') only supports the legacy 1-7 scale, so
        // insert size 7 as a marker then rewrite it to a real pixel size.
        document.execCommand('styleWithCSS', false, false);
        document.execCommand('fontSize', false, '7');
        bodyRef.current.querySelectorAll('font[size="7"]').forEach((el) => {
            el.removeAttribute('size');
            el.style.fontSize = `${px}px`;
        });
        syncBodyToState();
    };

    const applyColor = (command, color) => {
        bodyRef.current.focus();
        restoreSelection();
        if (command === 'hiliteColor') {
            if (!document.execCommand('hiliteColor', false, color)) {
                document.execCommand('backColor', false, color);
            }
        } else {
            document.execCommand(command, false, color);
        }
        syncBodyToState();
    };
    
    if (!isOpen) return null;

    return (
        <div className="email-modal-overlay">
            <div 
                ref={modalRef}
                className="email-modal-container resizable"
                style={{ 
                    width: '95vw',  // Use viewport width for initial size
                    height: '90vh', // Use viewport height for initial size
                    maxWidth: '1600px',
                    maxHeight: '1200px',
                    display: 'flex', // Add display flex
                    flexDirection: 'column' // Stack children vertically
                }}
            >
                <div className="email-modal-header">
                    <span className="email-modal-title">
                        {editEmail ? 'Edit Email' : 'New Email'}
                    </span>
                    <button 
                        type="button"
                        onClick={onClose}
                        className="email-modal-close"
                    >
                        ×
                    </button>
                </div>

                <form 
                    onSubmit={handleSubmit} 
                    className="email-modal-form"
                    style={{
                        flex: 1, // Allow form to fill available space
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0 // Prevent flexbox overflow issues
                    }}
                    onReset={(e) => {
                        e.preventDefault();
                        console.log('Form reset prevented');
                        return false;
                    }}
                    method="POST"
                    action="#"
                >
                    {/* Top Section - Email Fields and Right Side Sections */}
                    <div className="email-modal-top-section" style={{
                        flex: 1, // Allow this section to grow and shrink
                        overflowY: 'auto', // Add the main scrollbar here
                        minHeight: 0
                    }}>
                        {/* Left Section - Email Fields and Body */}
                        <div className="email-modal-email-fields">
                            {/* From and Date Row */}
                            <div className="email-modal-row">
                                <label className="email-modal-label">From:</label>
                                <input
                                    type="email"
                                    value={emailData.from || ''}
                                    onChange={(e) => setEmailData(prev => ({ ...prev, from: e.target.value }))}
                                    required
                                    className="email-modal-input flex-1 from-field"
                                    placeholder="your.email@company.com"
                                    readOnly={true}
                                />
                                <label className="email-modal-label">Date:</label>
                                <input
                                    type="date"
                                    value={emailData.date}
                                    onChange={(e) => setEmailData(prev => ({ ...prev, date: e.target.value }))}
                                    className="email-modal-input flex-1"
                                    readOnly={true}
                                />
                            </div>

                            {/* To Row */}
                            <div className="email-modal-row">
                                <label className="email-modal-label">To:</label>
                                <input
                                    type="text"
                                    value={emailData.to}
                                    onChange={(e) => setEmailData(prev => ({ ...prev, to: e.target.value }))}
                                    required
                                    className="email-modal-input flex-1 to-field"
                                    placeholder="recipient@email.com; multiple@emails.com"
                                    readOnly={readOnly}
                                />
                            </div>

                            {/* CC Row */}
                            <div className="email-modal-row">
                                <label className="email-modal-label">CC:</label>
                                <input
                                    type="text"
                                    value={emailData.cc || ''}
                                    onChange={(e) => setEmailData(prev => ({ ...prev, cc: e.target.value }))}
                                    className="email-modal-input flex-1 cc-field"
                                    placeholder="cc@email.com; multiple@emails.com"
                                    readOnly={readOnly}
                                />
                            </div>

                            {/* Email Template Row */}
                            {!readOnly && (
                                <div className="email-modal-row email-template-row">
                                    <label className="email-modal-label">Email Template:</label>
                                    <div className="email-template-select-container">
                                        <Select2Dropdown
                                            options={emailTemplates}
                                            value={emailData.selectedTemplateId || ''}
                                            onChange={handleTemplateSelect}
                                            placeholder={loadingTemplates ? 'Loading templates...' : 'Select a template (optional)'}
                                            isSearchable={true}
                                            isDisabled={loadingTemplates}
                                            className="email-template-select2"
                                            name="emailTemplate"
                                            displayKey="template_name"
                                            valueKey="id"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Bank / Cash selector — only when provided */}
                            {!readOnly && bankOptions && bankOptions.length > 0 && (
                                <div className="email-modal-row">
                                    <label className="email-modal-label">Bank:</label>
                                    <div className="email-template-select-container">
                                        <Select2Dropdown
                                            options={bankOptions}
                                            value={selectedBank?.id || ''}
                                            onChange={handleBankChange}
                                            placeholder="Select bank / cash..."
                                            isSearchable={true}
                                            clearable
                                            name="bankSelect"
                                            displayKey="text"
                                            valueKey="id"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Subject Row */}
                            <div className="email-modal-row">
                                <label className="email-modal-label">Subject:</label>
                                <input
                                    type="text"
                                    value={emailData.subject}
                                    onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                                    required
                                    className="email-modal-input flex-1"
                                    placeholder="Email subject"
                                    readOnly={readOnly}
                                />
                            </div>

                            {/* Selected Attachments */}
                            {(() => {
                                const items = [];
                                if (fixedAttachment && fixedAttachment.name) {
                                    items.push({ key: 'fixed', name: fixedAttachment.name, source: fixedAttachment });
                                }
                                (emailData.selectedJobAttachments || []).forEach((a, i) => {
                                    items.push({ key: `job-${a.id ?? i}`, name: getAttachmentDisplayName(a), source: a });
                                });
                                (emailData.attachments || []).forEach((f, i) => {
                                    items.push({ key: `file-${i}`, name: f.name, source: f });
                                });
                                if (items.length === 0) return null;
                                return (
                                    <div className="email-modal-selected-attachments">
                                        <span className="sel-label">Attachments:</span>
                                        <span>
                                            {items.map((item, i) => (
                                                <React.Fragment key={item.key}>
                                                    {i > 0 && ', '}
                                                    <a
                                                        href="#"
                                                        className="sel-attachment-link"
                                                        onClick={(e) => previewSelectedAttachment(e, item.source)}
                                                    >
                                                        {item.name}
                                                    </a>
                                                </React.Fragment>
                                            ))}
                                        </span>
                                    </div>
                                );
                            })()}

                            {/* Body Section - Now in the left column */}
                            <div className="email-modal-body-section">
                                <label className="email-modal-label">Body:</label>
                                
                                {!readOnly && (
                                    <div className="email-modal-toolbar">
                                        <button type="button" onClick={() => formatEmailBody('bold')} title="Bold" className="toolbar-btn">
                                            <b>B</b>
                                        </button>
                                        <button type="button" onClick={() => formatEmailBody('italic')} title="Italic" className="toolbar-btn">
                                            <i>I</i>
                                        </button>
                                        <button type="button" onClick={() => formatEmailBody('underline')} title="Underline" className="toolbar-btn">
                                            <u>U</u>
                                        </button>
                                        <button type="button" onClick={() => formatEmailBody('insertUnorderedList')} title="Bullets" className="toolbar-btn">
                                            •
                                        </button>
                                        <button type="button" onClick={() => formatEmailBody('insertOrderedList')} title="Numbered List" className="toolbar-btn">
                                            1.
                                        </button>
                                        <button type="button" onClick={() => formatEmailBody('formatBlock', 'blockquote')} title="Quote" className="toolbar-btn">
                                            "
                                        </button>
                                        <button type="button" onClick={() => formatEmailBody('removeFormat')} title="Clear Formatting" className="toolbar-btn">
                                            ✗
                                        </button>
                                        <select
                                            className="toolbar-select"
                                            value={fontFamily}
                                            title="Font"
                                            onMouseDown={saveSelection}
                                            onChange={(e) => applyFontName(e.target.value)}
                                        >
                                            {(FONT_OPTIONS.includes(fontFamily) ? FONT_OPTIONS : [fontFamily, ...FONT_OPTIONS]).map((f) => (
                                                <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                                            ))}
                                        </select>
                                        <select
                                            className="toolbar-select toolbar-select-size"
                                            value={fontSize}
                                            title="Font Size"
                                            onMouseDown={saveSelection}
                                            onChange={(e) => applyFontSize(e.target.value)}
                                        >
                                            <option value="" disabled>Size</option>
                                            {FONT_SIZES.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                        <label className="toolbar-color-label" title="Font Colour" onMouseDown={saveSelection}>
                                            <span className="toolbar-color-icon" style={{ borderBottomColor: '#c00000' }}>A</span>
                                            <input
                                                type="color"
                                                className="toolbar-color-input"
                                                defaultValue="#000000"
                                                onChange={(e) => applyColor('foreColor', e.target.value)}
                                            />
                                        </label>
                                        <label className="toolbar-color-label" title="Text Highlight Colour" onMouseDown={saveSelection}>
                                            <span className="toolbar-color-icon" style={{ borderBottomColor: '#ffc000', background: '#fff3b0' }}>ab</span>
                                            <input
                                                type="color"
                                                className="toolbar-color-input"
                                                defaultValue="#ffff00"
                                                onChange={(e) => applyColor('hiliteColor', e.target.value)}
                                            />
                                        </label>
                                    </div>
                                )}

                                <div
                                    ref={bodyRef}
                                    contentEditable={!readOnly}
                                    className={`email-modal-body ${readOnly ? 'read-only' : ''}`}
                                    onInput={handleBodyInput}
                                    onBlur={handleBodyInput}
                                    onKeyDown={handleBodyKeyDown}  // Add this
                                    onPaste={handleBodyPaste}
                                    suppressContentEditableWarning={true}
                                    style={{
                                        direction: 'ltr',
                                        unicodeBidi: 'embed',
                                        lineHeight: '1.5'  // Add this for better readability
                                    }}
                                />
                            </div>
                        </div>

                        {/* Right Section - Customer Email Groups and Job Attachments */}
                        <div className="email-modal-right-section">
                            {/* Customer Email Groups Section */}
                            {customerEmailGroups && customerEmailGroups.length > 0 && !readOnly && (
                                <div className="email-modal-groups-section">
                                    <label className="email-modal-label">Customer Email Groups:</label>
                                    <div className="email-groups-container">
                                        <div className="email-groups-header">
                                            <span className="header-to">To</span>
                                            <span className="header-cc">CC</span>
                                            <span className="header-group">Group</span>
                                            <span className="header-emails">Emails</span>
                                        </div>
                                        <div className="email-groups-list">
                                            {customerEmailGroups.map((group, index) => (
                                                <div key={index} className="email-group-item">
                                                    <div className="group-to-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedGroups[`${index}_to`] || false}
                                                            onChange={(e) => handleEmailGroupSelection(group.emails, 'to', index, e.target.checked)}
                                                        />
                                                    </div>
                                                    <div className="group-cc-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedGroups[`${index}_cc`] || false}
                                                            onChange={(e) => handleEmailGroupSelection(group.emails, 'cc', index, e.target.checked)}
                                                        />
                                                    </div>
                                                    <div className="group-name">
                                                        {group.group_name}
                                                    </div>
                                                    <div className="group-emails">
                                                        {group.emails}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Job Attachments Section */}
                            {jobAttachments && jobAttachments.length > 0 && (() => {
                                const docTypes = [...new Set(
                                    jobAttachments.map(a => a.document_type || a.type).filter(Boolean)
                                )].sort();
                                const filteredAttachments = (docTypeFilter
                                    ? jobAttachments.filter(a => (a.document_type || a.type) === docTypeFilter)
                                    : jobAttachments
                                ).slice().sort((a, b) => {
                                    const aIdx = selectedAttachmentOrder.indexOf(a.id);
                                    const bIdx = selectedAttachmentOrder.indexOf(b.id);
                                    if (aIdx === -1 && bIdx === -1) return 0;
                                    if (aIdx === -1) return 1;
                                    if (bIdx === -1) return -1;
                                    return aIdx - bIdx;
                                });
                                return (
                                <div className="email-modal-job-attachments-section">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                                        <label className="email-modal-label" style={{ marginBottom: 0 }}>Job Attachments:</label>
                                        <div style={{ minWidth: 220 }}>
                                            <Select2Dropdown
                                                options={docTypes.map(t => ({ id: t, text: t }))}
                                                value={docTypeFilter}
                                                onChange={val => setDocTypeFilter(val || '')}
                                                valueKey="id"
                                                displayKey="text"
                                                placeholder="All Document Types"
                                                clearable={true}
                                                disabled={readOnly}
                                                popoutMode={true}
                                            />
                                        </div>
                                    </div>
                                    <div className="job-attachments-container">
                                        <div className="job-attachments-header">
                                            <span className="header-select">Select</span>
                                            <span className="header-name">Document Name</span>
                                            <span className="header-size">Document Size</span>
                                            <span className="header-for">Document Type</span>
                                        </div>
                                        <div className="job-attachments-list">
                                            {filteredAttachments.map((attachment, index) => {
                                                const getFileName = (attachment) => {
                                                    let fileName = '';
                                                    
                                                    if (attachment.document_file) {
                                                        if (typeof attachment.document_file === 'string') {
                                                            const urlParts = attachment.document_file.split('/');
                                                            fileName = urlParts[urlParts.length - 1];
                                                        } else {
                                                            fileName = attachment.document_file.name || attachment.document_file;
                                                        }
                                                    } else {
                                                        fileName = attachment.file_name || attachment.name || 'Unknown File';
                                                    }

                                                    if (fileName.includes('.')) {
                                                        const parts = fileName.split('.');
                                                        const extension = parts.pop();
                                                        const nameWithoutExt = parts.join('.');
                                                        const cleanName = nameWithoutExt.replace(/_[a-zA-Z0-9]+$/, '');
                                                        return `${cleanName}.${extension}`;
                                                    }
                                                    
                                                    return fileName;
                                                };

                                                const formatFileSize = (bytes) => {
                                                    if (!bytes || bytes === 0) return 'N/A';
                                                    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                                                    const i = Math.floor(Math.log(bytes) / Math.log(1024));
                                                    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
                                                };

                                                const getDocumentUrl = (attachment) => {
                                                    if (attachment.document_file_url) {
                                                        return attachment.document_file_url;
                                                    }
                                                    if (attachment.document_file && typeof attachment.document_file === 'string') {
                                                        return attachment.document_file;
                                                    }
                                                    if (attachment.url) {
                                                        return attachment.url;
                                                    }
                                                    if (attachment.attach_url) {
                                                        return attachment.attach_url;
                                                    }
                                                    return null;
                                                };

                                                const previewAttachment = async (e, url, name) => {
                                                    if (!previewAttachments) return;
                                                    e.preventDefault();
                                                    try {
                                                        const res = await fetch(url, { headers: getAuthHeaders() });
                                                        if (!res.ok) throw new Error('Failed to load document.');
                                                        const blob = await res.blob();
                                                        const blobUrl = URL.createObjectURL(blob);
                                                        const previewWindow = openFilePreview(blobUrl, name);
                                                        if (previewWindow && previewWindow.addEventListener) {
                                                            previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(blobUrl), { once: true });
                                                        } else {
                                                            setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
                                                        }
                                                    } catch (err) {
                                                        toast.error(err.message || 'Could not open document.');
                                                    }
                                                };

                                                const fileName = getFileName(attachment);
                                                const fileSize = formatFileSize(attachment.file_size);
                                                const documentUrl = getDocumentUrl(attachment);

                                                return (
                                                    <div key={attachment.id || index} className="job-attachment-item">
                                                        <div className="attachment-select">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedJobAttachmentIds.has(attachment.id)}
                                                                onChange={(e) => handleJobAttachmentSelection(attachment, e.target.checked)}
                                                                disabled={readOnly}
                                                            />
                                                        </div>
                                                        <div className="attachment-name">
                                                            {documentUrl ? (
                                                                <a 
                                                                    href={documentUrl} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    onClick={(e) => previewAttachment(e, documentUrl, fileName)}
                                                                    title={`Open ${fileName}`}
                                                                >
                                                                    {fileName}
                                                                </a>
                                                            ) : (
                                                                <span className="no-link">{fileName}</span>
                                                            )}
                                                        </div>
                                                        <div className="attachment-size">
                                                            {fileSize}
                                                        </div>
                                                        <div className="attachment-for">
                                                            {attachment.document_type || attachment.type || 'N/A'}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {filteredAttachments.length === 0 && (
                                                <div className="job-attachment-item">
                                                    <span className="no-link" style={{ padding: 8 }}>No attachments match this document type.</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                );
                            })()}
                        </div>
                    </div>
                    
                    {/* Bottom Section - File Attachments and Actions */}
                    <div className="email-modal-bottom-section">
                        <div className="email-modal-bottom-row">
                            {fixedAttachment && (
                                <div className="email-modal-attachments">
                                    <label className="email-modal-label">Attachment:</label>
                                    <div className="email-modal-file-info">
                                        <i className="fa fa-paperclip" style={{ marginRight: 6 }} />
                                        {fixedAttachment.url ? (
                                            <a
                                                href={fixedAttachment.url}
                                                onClick={async (e) => {
                                                    e.preventDefault();
                                                    try {
                                                        const res = await fetch(fixedAttachment.url, { headers: getAuthHeaders(false) });
                                                        if (!res.ok) throw new Error('Failed to load attachment.');
                                                        const blob = await res.blob();
                                                        const blobUrl = URL.createObjectURL(blob);
                                                        const previewWindow = openFilePreview(blobUrl, fixedAttachment.name);
                                                        if (previewWindow && previewWindow.addEventListener) {
                                                            previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(blobUrl), { once: true });
                                                        } else {
                                                            setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
                                                        }
                                                    } catch (err) {
                                                        toast.error(err.message || 'Could not open attachment.');
                                                    }
                                                }}
                                            >
                                                {fixedAttachment.name}
                                            </a>
                                        ) : (
                                            <span>{fixedAttachment.name}</span>
                                        )}
                                        <span style={{ marginLeft: 6, color: '#888' }}>(will be attached automatically)</span>
                                    </div>
                                </div>
                            )}
                            {showAttachments && (
                                <div className="email-modal-attachments">
                                    <label className="email-modal-label">File Attachments:</label>
                                    <input
                                        type="file"
                                        multiple
                                        onChange={handleAttachmentChange}
                                        className="email-modal-file-input"
                                        disabled={readOnly}
                                    />
                                    {emailData.attachments && emailData.attachments.length > 0 && (
                                        <div className="email-modal-file-info">
                                            {emailData.attachments.length} file(s) selected
                                        </div>
                                    )}
                                    {emailData.selectedJobAttachments && emailData.selectedJobAttachments.length > 0 && (
                                        <div className="email-modal-file-info">
                                            {emailData.selectedJobAttachments.length} job attachment(s) selected
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="email-modal-actions">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="email-modal-btn email-modal-btn-secondary"
                                >
                                    Cancel
                                </button>
                                {!readOnly && (
                                    <button
                                        type="button"
                                        className="email-modal-btn email-modal-btn-primary"
                                        onClick={(e) => {
                                            console.log('Send button clicked directly');
                                            const safeEvent = {
                                                ...e,
                                                preventDefault: e.preventDefault || (() => {}),
                                                stopPropagation: e.stopPropagation || (() => {}),
                                                stopImmediatePropagation: e.stopImmediatePropagation || (() => {})
                                            };
                                            handleSubmit(safeEvent);
                                        }}
                                    >
                                        {editEmail ? 'Update' : 'Send'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
                <div
                    className="email-modal-resize-handle"
                    onMouseDown={() => setIsResizing(true)}
                />
                <ConfirmDialog
                    open={confirmNoAttachments}
                    title="No Attachments"
                    message={
                        <>
                            <strong style={{ color: '#dc2626' }}>NO ATTACHMENTS</strong>
                            {' have been selected. Do you want to send the email '}
                            <span style={{ color: '#dc2626' }}>WITHOUT ATTACHMENTS</span>
                            ?
                        </>
                    }
                    onConfirm={() => {
                        setConfirmNoAttachments(false);
                        const pendingEvent = pendingSubmitRef.current;
                        pendingSubmitRef.current = null;
                        handleSubmit(pendingEvent, true);
                    }}
                    onCancel={() => {
                        setConfirmNoAttachments(false);
                        pendingSubmitRef.current = null;
                    }}
                />
            </div>
        </div>
    );
};

export default EmailModal;